import * as React from 'react';
import { UserConditionModel } from "../../../models/user/userConditionModel";
import * as HelperFunctions from '../../../common/functions/helperFunctions';
import { UserConditionType, GenderType } from '../../../common/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import CSSTransition from 'react-transition-group/CSSTransition';
import Translate from '../../../common/functions/translate';
import { ResponsiveLine } from '@nivo/line';
import color from '@material-ui/core/colors/orange';
import { area, curveMonotoneX } from "d3-shape";
import { ConditionUnitRangeModel } from '../../../models/user/conditionUnitRangeModel';
import { number } from 'prop-types';

class LineModel {
  public id: string;
  //public color: string;
  public data: Array<LineDotModel>;
}

class LineDotModel {
  public x?: string;
  public y: number;
}

let minBorder: any;
let maxBorder: any;

//DEFAULT NIVO COLORS #e8c1a0 #f47560
const firstLineColor = "#f98866";
const secondLineColor = "#ffbb00";

const chartData = (userCondition: UserConditionModel, lang: string, typeRanges: ConditionUnitRangeModel) => {
  let arr = new Array<LineModel>();
  let label = userCondition.isSystemType ? HelperFunctions.getEnumLabel(userCondition.userConditionTypeId, UserConditionType, true) +
    ' (' + userCondition.userConditionUnit + ')' + '' : userCondition.personalCondition + '';
  if (userCondition.isSystemType && userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
    let topBLine = {
      id: Translate('TopNumber') + ' (' + userCondition.userConditionUnit + ')',
      data: new Array<LineDotModel>(0),
      style: {
        strokeWidth: 10,
      }
    }

    let bottomBLine = {
      id: Translate('BottomNumber') + ' (' + userCondition.userConditionUnit + ')',
      data: new Array<LineDotModel>(0),
      style: {
        strokeWidth: 10,
      }
    }
    userCondition.userConditionTypes = userCondition.userConditionTypes.sort((a, b) => new Date(a.conditionDate).getTime() - new Date(b.conditionDate).getTime());
    for (let uType of userCondition.userConditionTypes) {
      let bPressure = uType.userConditionValue.split('/');
      topBLine.data.push({
        x: HelperFunctions.fomatDateTimeSecondsAsStringWithLang(new Date(uType.conditionDate), lang) + '',
        y: parseFloat(bPressure[0])
      });

      bottomBLine.data.push({
        x: HelperFunctions.fomatDateTimeSecondsAsStringWithLang(new Date(uType.conditionDate), lang) + '',
        y: parseFloat(bPressure[1])
      });
    }
    arr.push(topBLine);
    arr.push(bottomBLine);
  }
  else {
    let newLine = {
      id: label,
      data: new Array<LineDotModel>(0),
      style: {
        strokeWidth: 10
      }
    }

    let sortedItems = userCondition.userConditionTypes.sort((a, b) => new Date(a.conditionDate).getTime() - new Date(b.conditionDate).getTime());
    for (let uType of sortedItems) {
      newLine.data.push({
        x: HelperFunctions.fomatDateTimeSecondsAsStringWithLang(new Date(uType.conditionDate), lang) + '',
        y: parseFloat(uType.userConditionValue)
      });
    }

    arr.push(newLine);
  }
  return arr;
}

const getTspanGroups = (value: string, maxLineLength: number, maxLines: number = 2) => {
  const words = value.split(' ')

  type linesAcc = {
    lines: string[],
    currLine: string
  }

  //reduces the words into lines of maxLineLength
  const assembleLines: linesAcc = words.reduce((acc: linesAcc, word: string) => {
    //if the current line isn't empty and the word + current line is larger than the allowed line size, create a new line and update current line
    if ((word + acc.currLine).length > maxLineLength && acc.currLine !== '') {
      return {
        lines: acc.lines.concat([acc.currLine]),
        currLine: word
      }
    }
    //otherwise add the word to the current line
    return {
      ...acc,
      currLine: acc.currLine + ' ' + word
    }

  }, { lines: [], currLine: '' })

  //add the ending state of current line (the last line) to lines
  const allLines = assembleLines.lines.concat([assembleLines.currLine])

  //for now, only take first 2 lines due to tick spacing and possible overflow
  const lines = allLines.slice(0, maxLines)
  let children: JSX.Element[] = []
  let dy = 0

  lines.forEach((lineText, i) => {
    children.push(
      <tspan x={0} dy={dy} key={i}>
        {
          // if on the second line, and that line's length is within 3 of the max length, add ellipsis
          (1 === i && allLines.length > 2) ? lineText.slice(0, maxLineLength - 3) + '...' : lineText
        }
      </tspan>
    )
    //increment dy to render next line text below
    dy += 15
  });

  return children
}

const getMinAndMax = (userCondition: UserConditionModel, isMin: boolean, minMarker: number, maxMarker: number, isThereMarkers: boolean) => {
  let val: number = 0;
  if (userCondition.isSystemType && userCondition.userConditionTypeId == UserConditionType.BloodPressure) {
    if (isThereMarkers) {
      val = isMin ? minMarker : maxMarker
    }
    else {
      let blPressure = userCondition.userConditionTypes[0].userConditionValue.split('/');
      val = isMin ? +blPressure[1] : +blPressure[0];
    }
    for (let uType of userCondition.userConditionTypes) {
      let bPressure = uType.userConditionValue.split('/');
      if (isMin) {
        val = +bPressure[1] < val ? +bPressure[1] : val;
      }
      else {
        val = +bPressure[0] > val ? +bPressure[0] : val;
      }
    }
  }
  else {
    if (isThereMarkers) {
      val = isMin ? minMarker : maxMarker
    }
    else {
      val = +userCondition.userConditionTypes[0].userConditionValue;
    }
    for (let uType of userCondition.userConditionTypes) {
      if (isMin) {
        val = +uType.userConditionValue < val ? +uType.userConditionValue : val;
      }
      else {
        val = +uType.userConditionValue > val ? +uType.userConditionValue : val;
      }
    }

  }
  return val;
}

var currentPointFromLine = 0;

const getPointColor = (data, props) => {
  let typeRange: ConditionUnitRangeModel = props.typeRanges;
  let color = "#7bc87b";

  if (typeRange && data && data.data[currentPointFromLine] && data.data[currentPointFromLine].data && data.color == firstLineColor) {
    if (typeRange.minValue && typeRange.minValue > data.data[currentPointFromLine].data.y ||
      typeRange.maxValue && typeRange.maxValue < data.data[currentPointFromLine].data.y) {
      color = "#ff4d4d";
    }
    currentPointFromLine++;
  }

  if (typeRange && data && data.data[currentPointFromLine] && data.data[currentPointFromLine].data && data.color == secondLineColor) {
    currentPointFromLine++;
    if (typeRange.secondMinValue && typeRange.secondMinValue > data.data[currentPointFromLine - 1].data.y ||
      typeRange.secondMaxValue && typeRange.secondMaxValue < data.data[currentPointFromLine - 1].data.y) {
      color = "#ff4d4d";
    }
  }

  if (currentPointFromLine >= data.data.length) {
    currentPointFromLine = 0;
  }

  return color;
}

const UserConditionCharts = (props) => {
  let typeRange: ConditionUnitRangeModel = props.typeRanges;
  let data = chartData(props.userCondition, props.lang, props.typeRanges);

  let markers = new Array<any>(0);
  let minMarker: number = 0;
  let maxMarker: number = 0;

  if (typeRange) {
    minMarker = +typeRange.minValue;
    maxMarker = +typeRange.maxValue;

    if (typeRange.minValue) {
      markers.push({
        axis: "y", value: +typeRange.minValue,
        lineStyle: { stroke: '#0aa5ff', strokeDasharray: "12, 6" }, legend: "min: " + typeRange.minValue, textStyle: { fontSize: 10 }
      });
      if (+typeRange.minValue < minMarker) {
        minMarker = +typeRange.minValue;
      }
    }
    if (typeRange.maxValue) {
      markers.push({
        axis: "y", value: +typeRange.maxValue,
        lineStyle: { stroke: '#0aa5ff', strokeDasharray: "12, 6" }, legend: "max: " + typeRange.maxValue, textStyle: { fontSize: 10 }
      });
      if (+typeRange.maxValue > maxMarker) {
        maxMarker = +typeRange.maxValue;
      }
    }

    if (typeRange.secondMinValue) {
      markers.push({
        axis: "y", value: +typeRange.secondMinValue,
        lineStyle: { stroke: '#34675c', strokeDasharray: "12, 6" }, legend: "min: " + typeRange.secondMinValue, legendPosition: 'bottom-right', textStyle: { fontSize: 10 }
      });
      if (+typeRange.secondMinValue < minMarker) {
        minMarker = +typeRange.secondMinValue;
      }
    }
    if (typeRange.secondMaxValue) {
      markers.push({
        axis: "y", value: +typeRange.secondMaxValue,
        lineStyle: { stroke: '#34675c', strokeDasharray: "12, 6" }, legend: "max: " + typeRange.secondMaxValue, legendPosition: 'bottom-right', textStyle: { fontSize: 10 }
      });
      if (+typeRange.secondMaxValue > maxMarker) {
        maxMarker = +typeRange.secondMaxValue;
      }
    }
  }

  let minVal: number = getMinAndMax(props.userCondition, true, minMarker, maxMarker, markers && markers.length > 0);
  let maxVal: number = getMinAndMax(props.userCondition, false, minMarker, maxMarker, markers && markers.length > 0);

  let delta = maxVal - minVal;

  minBorder = minVal != 0 ? minVal - (delta / 5) : null;
  maxBorder = maxVal != 0 ? maxVal + (delta / 5) : null;

  //console.log(data);
  // {
  //   "tickSize": 5,
  //   "tickPadding": 5,
  //   "tickRotation": 0,
  //   "legend": "",
  //   "legendOffset": 36,
  //   "legendPosition": "middle"
  // }
  return (

    <ResponsiveLine
      data={data}
      lineWidth={3}
      margin={{
        "top": 30,
        "right": 30,
        "bottom": 50,
        "left": 35
      }}
      xScale={{
        "type": "point"
      }}
      yScale={{
        "type": "linear",
        "stacked": false,
        "min": minVal != 0 ? minVal - (delta / 5) : "auto",
        "max": maxVal != 0 ? maxVal + (delta / 5) : "auto"
      }}
      axisBottom={
        {
          "tickSize": 5,
          "tickPadding": 15,
          "tickRotation": 0,
          "legend": "",
          "legendOffset": 46,
          "legendPosition": "middle",
          renderTick: (data) => {
            const { value, opacity, textBaseline, textAnchor, textX, textY, x, y } = data
            return (

              <g key={value}
                transform={`translate(${x},${y})`}
                style={{ opacity }}
              >
                <text
                  alignmentBaseline={textBaseline}
                  style={{ fontSize: 10 }}
                  textAnchor={textAnchor}
                  transform={`translate(${textX},${textY})`}
                >
                  {getTspanGroups(value, 10, 3)}
                </text>
              </g>
            )
          }
        }
      }
      axisLeft={{
        "tickSize": 5,
        "tickPadding": 5,
        "tickRotation": 0,
        "legend": "count",
        "legendOffset": -40,
        "legendPosition": "middle"
      }}
      colors={[firstLineColor, secondLineColor]}
      curve='monotoneX'
      //dotSize={5}
      // dotBorderWidth={2}
      // dotBorderColor={{
      //   "from": "color"
      // }}
      //enableDotLabel={true}
      //useMesh={true}
      enableCrosshair={false}
      animate={false}
      motionStiffness={90}
      motionDamping={15}
      markers={markers}
      enableSlices="x"
      pointSize={10}
      //pointColor="#009700"
      pointColor={(data) => getPointColor(data, props)}
      pointBorderWidth={3}
      pointBorderColor="#ffffff"
      //layers={['grid', 'markers', 'areas', refsLayer, 'lines', 'slices', 'axes', 'points', 'legends']}
      legends={[
        {
          "anchor": "top-left",
          "direction": "row",
          "justify": false,
          "translateX": -20,
          "translateY": -30,
          "itemsSpacing": 0,
          "itemDirection": "left-to-right",
          "itemWidth": 160,
          "itemHeight": 20,
          "itemOpacity": 0.75,
          "symbolSize": 12,
          "symbolShape": "circle",
          "effects": [
            {
              "on": "hover",
              "style": {
                "itemBackground": "rgba(0, 0, 0, .03)",
                "itemOpacity": 1
              }
            }
          ]
        }
      ]}
    />
  )
}

const refsLayer = props => {
  console.log(props)
  if (props.markers && props.markers.length > 0) {
    if (props.markers.length == 2) {
      return <g>
        <rect
          y={props.yScale(maxBorder)}
          width={props.width}
          height={props.yScale(props.markers[1].value) - props.yScale(maxBorder)}
          fill="rgba(255,0,0,.15)"
        />
        {/* <rect
        y={props.yScale(37)}
        width={props.width}
        height={props.yScale(35) - props.yScale(37)}
        fill="rgba(0,255,0,.15)"
      /> */}
        <rect
          y={props.yScale(props.markers[0].value)}
          width={props.width}
          height={props.yScale(minBorder) - props.yScale(props.markers[0].value)}
          fill="rgba(255,0,0,.15)"
        />
      </g>
    }
    else {
      return <g>
        <rect
          y={props.yScale(maxBorder)}
          width={props.width}
          height={props.yScale(props.markers[1].value) - props.yScale(maxBorder)}
          fill="rgba(255,0,0,.15)"
        />
        {/* <rect
      y={props.yScale(37)}
      width={props.width}
      height={props.yScale(35) - props.yScale(37)}
      fill="rgba(0,255,0,.15)"
    /> */}
        <rect
          y={props.yScale(props.markers[0].value)}
          width={props.width}
          height={props.yScale(minBorder) - props.yScale(props.markers[0].value)}
          fill="rgba(255,0,0,.15)"
        />
        {/* <rect
      y={props.yScale(37)}
      width={props.width}
      height={props.yScale(35) - props.yScale(37)}
      fill="rgba(0,255,0,.15)"
    /> */}
        <rect
          y={props.yScale(props.markers[3].value)}
          width={props.width}
          height={props.yScale(minBorder) - props.yScale(props.markers[3].value)}
          fill="rgba(255,150,0,.15)"
        />
      </g>
    }
  }
}
  ;



export default UserConditionCharts;
